/* eslint-disable react/prop-types */
import React, { useCallback } from 'react';
import { Badge, Box, Chip, IconButton, InputAdornment, Menu, Stack, TextField, Theme, Tooltip } from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_VisibilityState,
} from 'material-react-table';
import {
  FilterProjectsWithLinkedReports,
  bulkDeleteProjects,
  exportProjectsCsv,
  fetchProjects,
  retrieveProjectDraws,
} from '../components/project/project.api';
import { capitalize, deepCopy, formatBudget, formatISODate, joinDateAndUser2 } from '../common/utils/common.util';
import { useQuery, useQueryClient } from 'react-query';
import dataKeys from '../common/react-query/data-keys';
import { Collapse } from '@mui/material';
import { handleError } from '../common/utils/util.errors';
import {
  Place,
  ViewList,
  Search,
  Clear,
  Person,
  Delete as DeleteIcon,
  Assessment,
  ViewColumnOutlined as ViewColumnIcon,
  FilterList as FilterListIcon,
  CloudUpload,
  Description,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import FilterPanel from '../components/custom-filter-panel/custom-filter-panel';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../common/constants/urls';
import { getFromLocalStorage, storeInLocalStorage } from '../common/utils/util.cache';
import { styled } from '@mui/system';
import { Button, Typography } from 'components/index';
import { useAuth } from 'contexts/AuthContext';
import WhiteFab from '../components/common/WhiteFab';
import { IconOverTextButton } from '../components/common/IconOverTextButton';
import { ButtonDivider } from '../components/common/ButtonDivider';
import { ReportGeneralInfo } from '../components/report/report.type';
import { isValidGeneralInfo } from '../components/report/report.util';
import { generateReport } from '../components/report/report.api';
import { getProjectFiltersConfig } from '../components/project/project.type';
import { ProjectWizard } from '../components/project/project-setup-wizard/ProjectWizard';
import { FilterConfig } from '../components/custom-filter-panel/DynamicFilters';
import { CustomTooltip } from '../components/common/CustomTooltip';
import { TAG_CONFIG } from '../components/project/project.util';
import useLookups from '../hooks/useLookups';
import { LOOKUP_KEYS } from '../common/constants/lookups';
import { City, County, CustomArea, State } from '../components/lookups/lookups.type';
import { mapCityLookup, mapCountyLookup, mapCustomAreaLookup, mapStateLookup } from '../components/lookups/lookups.util';
import ProjectListMap from '../components/map/ProjectListMap';
import MapQuickFilters, {
  MAP_QUICK_FILTER_DEFAULTS,
  MAP_QUICK_FILTER_EMPTY,
  MapQuickFilterValues,
} from '../components/map/MapQuickFilters';
import TabPanel from '../components/general/TabPanel';
import { NAVBAR_HEIGHT } from '../common/constants/general';
import { useDebounce } from '@uidotdev/usehooks';
import { buildSearchRequestBody, preProcessSearchText } from '../common/utils/azure-search.util';
import PreviewPopoverIcon from '../components/project/projects-list/PreviewPopoverIcon';
import { UnassignedBudgetCell } from '../components/project/projects-list/UnassignedBudgetCell';
import { deleteProject } from '../components/deleteItem/deleteItem.api';
import DeletionHandler from '../components/deleteItem/deleteItem';
import { toast } from 'react-toastify';
import { getAppliedFiltersCount } from '../components/custom-filter-panel/custom-filter-panel.util';
import { usePrompt } from '../components/AppPrompt';
import { PermissionGate } from '../components/common/PermissionGate';
import { useMRTStyling } from '../hooks/useMRTStyling';
import SelectedProjectChips from '../components/project/SelectedProjectChips';
import { CustomManageColumnsMenu } from '../components/common/CustomManageColumnsMenu';
import { getUserColumnPreferences, saveUserColumnPreferences, saveUserFilterPreferences } from '../components/project/project.api';
import { PAGE_KEYS } from '../components/report/constants';
import UploadDialog from '../components/file-upload/UploadDialog';
import { DrawOption } from '../components/file-upload/PayappUploadDialog';
import { useMagicReportDrawSelection } from '../hooks/useMagicReportDrawSelection';
import MagicReportDrawSelectDialog from '../components/project/MagicReportDrawSelectDialog';
import RequiredFieldsGateDialog from '../components/project/RequiredFieldsGateDialog';

const DEFAULT_PAGE_SIZE = 25;
const REPORT_PAGE_SIZE = 10;
const CONTROLS_HEIGHT = 85;
const DEBOUNCE_DELAY_MS = 500;
const cacheKey = `projects-table-preferences`;
const FILTERS_KEY = 'project-table-preferences';
const PAGE_KEY = PAGE_KEYS.PROJECTS_LIST;
const MAP_QUICK_FILTER_FIELDS = new Set(['Status', 'Observer', 'CustomArea', 'NetworkerStatus']);
const showMagicReport = process.env.REACT_APP_SHOW_MAGIC_REPORT === 'true';

type FilterItem = {
  field: string;
  operator: string;
  value: any;
};

type FilterModel = {
  items: FilterItem[];
  logicOperator: 'and' | 'or';
};

const DEFAULT_FILTERS: FilterModel = {
  items: [],
  logicOperator: 'and',
};

const getInitialFilters = (items: FilterItem[]) => {
  const savedFilters = getFromLocalStorage(FILTERS_KEY);
  return items
    ? {
        items: items,
        logicOperator: 'and',
      }
    : DEFAULT_FILTERS;
};

const saveFilters = (filters: FilterModel) => {
  storeInLocalStorage(FILTERS_KEY, filters);
};

const StyledDeleteIcon = styled(DeleteIcon)<{ color?: string }>(({ theme, color = 'gray' }) => ({
  color: color,
  '&:hover': {
    color: theme.palette.error.main,
  },
}));

const noRowsOverlay = () => (
  <Box display={'flex'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
    <Typography>No Projects Found</Typography>
  </Box>
);

type Project = {
  Id: string;
  ProjectNumber: string;
  ProjectTitle: string;
  Budget?: number;
  UnassignedBudget?: number;
  ProjectTags?: string[];
  ProjectTagDetails?: string[];
  CommencementDate?: string;
  RevisedCompletionDate?: string;
  Status?: string;
  CreatedOn?: string;
  CreatedBy?: string;
  UpdatedOn?: string;
  UpdatedBy?: string;
  ProjectType?: string;
  Observer?: string;
  City?: string;
  PreviewCoverImageBlobName?: string;
  IsDraft?: boolean;
  Contractor?: string;
  County?: string;
  CustomArea?: string;
  PostalCode?: string;
  ConstructionType?: string;
  Electrical?: string;
  ExteriorWalls?: string;
  Foundation?: string;
  HVACScope?: string;
  HVACType?: string;
  Roof?: string;
  Superstructure?: string;
  UniqueCostFactors1?: string;
  UniqueCostFactors2?: string;
  UniqueCostFactors3?: string;
  OtherCostFactor?: string;
  Windows?: string;
  Client?: string;
  Borrower?: string;
  MonitoringFee?: number;
  PlanOrCostFee?: number;
  OriginalCompletionDateEstimated?: string;
  PlanOrCostInvDate?: string;
  PlanOrCostReportDate?: string;
  Plumbing?: string;
  SiteArea?: number;
  StoryCount?: number;
  BuildingCount?: number;
  UnitCount?: number;
  ElevatorCount?: number;
  GrossBuildingArea?: number;
  NetArea?: number;
  MonitoringType?: string;
  NetworkerStatus?: string;
  '@search.highlights'?: {
    [key: string]: string[];
  };
};

const columnVisibilityModel: MRT_VisibilityState = {
  'createdBy.Id': false,
  CreatedBy: false,
  CreatedOn: false,
  UpdatedOn: false,
  Contractor: false,
  MonitoringFee: false,
  PlanOrCostFee: false,
  OriginalCompletionDateEstimated: false,
  PlanOrCostInvDate: false,
  PlanOrCostReportDate: false,
  County: false,
  CustomArea: false,
  PostalCode: false,
  ConstructionType: false,
  Electrical: false,
  ExteriorWalls: false,
  Foundation: false,
  HVACScope: false,
  HVACType: false,
  Plumbing: false,
  Roof: false,
  SuperStructure: false,
  UniqueCostFactors1: false,
  UniqueCostFactors2: false,
  UniqueCostFactors3: false,
  OtherCostFactor: false,
  Windows: false,
  SiteArea: false,
  StoryCount: false,
  BuildingCount: false,
  UnitCount: false,
  ElevatorCount: false,
  GrossBuildingArea: false,
  NetArea: false,
  ProjectNumber: true,
  ProjectTitle: true,
  CommencementDate: true,
  CompletionDate: true,
  Status: true,
  ProjectType: true,
  Observer: true,
  Budget: true,
  UnassignedBudget: false,
  Address: true,
  Client: false,
  Borrower: false,
  ProjectTags: true,
  // Add new columns with default visibility
  Superstructure: false,
};

const getInitialState = (isFromReport = false) => ({
  density: 'compact' as const,
  columnVisibility: {
    ...columnVisibilityModel,
    UnassignedBudget: isFromReport,
    // Add new columns with default visibility
    Contractor: false,
    County: false,
    CustomArea: false,
    PostalCode: false,
    ConstructionType: false,
    Electrical: false,
    ExteriorWalls: false,
    Foundation: false,
    HVACScope: false,
    HVACType: false,
    Roof: false,
    Superstructure: false,
    UniqueCostFactors1: false,
    UniqueCostFactors2: false,
    UniqueCostFactors3: false,
    OtherCostFactor: false,
    Windows: false,
    Client: false,
    Borrower: false,
    // Add other columns that might be missing
    MonitoringFee: false,
    PlanOrCostFee: false,
    OriginalCompletionDateEstimated: false,
    PlanOrCostInvDate: false,
    PlanOrCostReportDate: false,
    Plumbing: false,
    SiteArea: false,
    StoryCount: false,
    BuildingCount: false,
    UnitCount: false,
    ElevatorCount: false,
    GrossBuildingArea: false,
    NetArea: false,
    'createdBy.Id': false,
  },
});

type ProjectsListProps = {
  isFromReport?: boolean;
  onProjectsSelect?: (selectedProjects: string[], filterString?: string) => void;
  disabledProjectsIds?: string[];
  defaultOnDisplayFilters?: string[];
  prevAppliedFilters?: string;
  shouldRenderMinimalActions?: boolean;
  projectSearchQuery?: string;
  disableChecboxSelectionOnMaps?: boolean;
  enableSingleSelection?: boolean;
  initialSelectedProjects?: readonly string[];
  customHeight?: number;
  resolvedProjectDetails?: Record<string, { number: string; title: string }>;
};

function ProjectsList({
  isFromReport = false,
  onProjectsSelect,
  disabledProjectsIds,
  defaultOnDisplayFilters,
  prevAppliedFilters,
  shouldRenderMinimalActions,
  projectSearchQuery,
  disableChecboxSelectionOnMaps,
  enableSingleSelection = false,
  initialSelectedProjects,
  customHeight,
  resolvedProjectDetails,
}: ProjectsListProps) {
  const { user } = useAuth();
  const { commonMRTStyling, getRowStyling, getActionsColumnStyling, getOnSortingChangeWithToast } = useMRTStyling<Project>();
  const [tabIndex, setTabIndex] = React.useState(0);
  const initialPageSize = isFromReport ? REPORT_PAGE_SIZE : DEFAULT_PAGE_SIZE;
  const [paginationModel, setPaginationModel] = React.useState({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [sortingModel, setSortingModel] = React.useState([{ id: 'UpdatedOn', desc: true }]);
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = React.useState<string>(projectSearchQuery || '');
  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_DELAY_MS);
  const [filterString, setFilterString] = React.useState<any[]>([]);
  const [rawFilters, setRawFilters] = React.useState<any>(undefined);
  const [savedFilterPrefs, setSavedFilterPrefs] = React.useState<string | undefined>(undefined);
  const [savedFilterPrefsNorm, setSavedFilterPrefsNorm] = React.useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const queryClient = useQueryClient();
  const [projectToDelete, setProjectToDelete] = React.useState<Project | null>(null);
  const { getConfirmation } = usePrompt();
  const [selectedProjects, setSelectedProjects] = React.useState<string[]>(initialSelectedProjects ? [...initialSelectedProjects] : []);
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>(
    initialSelectedProjects
      ? initialSelectedProjects.reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as MRT_RowSelectionState)
      : {},
  );
  const [toBeDeleted, setToBeDeleted] = React.useState<string[]>([]);
  const [generateReportInProgress, setGenerateReportInProgress] = React.useState(false);
  const [loadingRows, setLoadingRows] = React.useState<Set<string>>(new Set());
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = React.useState(false);
  const [csvExportInProgress, setCsvExportInProgress] = React.useState(false);
  const [payappUploadDialogOpen, setPayappUploadDialogOpen] = React.useState(false);
  const [payappUploadProject, setPayappUploadProject] = React.useState<{
    id: string;
    projectNumber: string;
    projectName: string;
  } | null>(null);
  const [payappUploadDraws, setPayappUploadDraws] = React.useState<DrawOption[]>([]);
  const [payappUploadRowId, setPayappUploadRowId] = React.useState<string | null>(null);
  const [magicReportRowId, setMagicReportRowId] = React.useState<string | null>(null);
  const {
    openDrawSelection,
    confirmDrawSelection,
    closeDialog: handleCloseMagicReportDrawSelect,
    dialogOpen: magicReportDrawSelectOpen,
    draws: magicReportDraws,
    project: magicReportProject,
    existingDrawIds: magicReportExistingDrawIds,
    existingHiddenCoLienDrawIds: magicReportExistingHiddenCoLienDrawIds,
    hasExistingReport: hasExistingMagicReport,
    isOpening: magicReportOpening,
    requiredFieldsGap,
    requiredFieldsGapProjectId,
    requiredFieldsGapProjectSummary,
    requiredFieldsGapOpen,
    retryAfterFieldsFixed,
    closeRequiredFieldsGap,
  } = useMagicReportDrawSelection();
  const [columnVisibility, setColumnVisibility] = React.useState<MRT_VisibilityState>(getInitialState(isFromReport).columnVisibility);
  const defaultPinning = React.useMemo(() => ({ left: ['mrt-row-select', 'ProjectNumber', 'ProjectTitle'], right: ['actions'] }), []);
  const [columnPinning, setColumnPinning] = React.useState<{ left?: string[]; right?: string[] }>(defaultPinning);
  const savePrefTimeout = React.useRef<any>(null);
  const rowCountRef = React.useRef(0);
  const [selectedProjectDetails, setSelectedProjectDetails] = React.useState<Record<string, { number: string; title: string }>>({});

  const stateOptions = useLookups(LOOKUP_KEYS.States) as State[];
  const cityOptions = useLookups(LOOKUP_KEYS.Cities) as City[];
  const countyOptions = useLookups(LOOKUP_KEYS.Counties) as County[];
  const customAreaOptions = useLookups(LOOKUP_KEYS.CustomAreas) as CustomArea[];

  // Fetch users for observer dropdown
  const { data: users = [] } = useQuery(
    'observers',
    async () => {
      const { getAllObservers } = await import('../components/project/project.api');
      return getAllObservers();
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );

  const projectFiltersConfig = getProjectFiltersConfig({
    stateOptions: stateOptions.map(mapStateLookup),
    cityOptions: cityOptions.map(mapCityLookup),
    countyOptions: countyOptions.map(mapCountyLookup),
    customAreaOptions: customAreaOptions.map(mapCustomAreaLookup),
    userOptions: users.map((user: any) => ({
      value: `${user.firstName} ${user.lastName}`,
      label: `${user.firstName} ${user.lastName}`,
    })),
  });
  const mapsEnabled = process.env.REACT_APP_GOOGLE_MAPS_ENABLED == 'true';
  const searchHidden = process.env.REACT_APP_SEARCH_HIDDEN == 'true';
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>(
    Object.entries(getInitialState(isFromReport).columnVisibility)
      .filter(([, isVisible]) => isVisible)
      .map(([column]) => column),
  );

  React.useEffect(() => {
    if (projectSearchQuery !== undefined) {
      setSearchQuery(projectSearchQuery);
    }
  }, [projectSearchQuery]);

  const [mapQuickFilters, setMapQuickFilters] = React.useState<MapQuickFilterValues>(MAP_QUICK_FILTER_DEFAULTS);

  const isQuickFilterString = (f: string): boolean => {
    return (
      f.startsWith('(Status eq') || f.startsWith('(Observer eq') || f.startsWith('(CustomArea eq') || f.startsWith('(NetworkerStatus eq')
    );
  };

  const escapeODataString = (v: string): string => v.replace(/'/g, "''");

  const buildQuickFilterStrings = (qf: MapQuickFilterValues): string[] => {
    const parts: string[] = [];
    if (qf.Status.length > 0) {
      parts.push(`(${qf.Status.map(v => `Status eq '${escapeODataString(v)}'`).join(' or ')})`);
    }
    if (qf.Observer.length > 0) {
      parts.push(`(${qf.Observer.map(v => `Observer eq '${escapeODataString(v)}'`).join(' or ')})`);
    }
    if (qf.CustomArea.length > 0) {
      parts.push(`(${qf.CustomArea.map(v => `CustomArea eq '${escapeODataString(v)}'`).join(' or ')})`);
    }
    if (qf.NetworkerStatus.length > 0) {
      parts.push(`(${qf.NetworkerStatus.map(v => `NetworkerStatus eq '${escapeODataString(v)}'`).join(' or ')})`);
    }
    return parts;
  };

  const handleTabChange = (newValue: any) => {
    setTabIndex(newValue);
    if (newValue === 1) {
      // Entering map view: always reapply the default quick filters
      setMapQuickFilters(MAP_QUICK_FILTER_DEFAULTS);
      const quickParts = buildQuickFilterStrings(MAP_QUICK_FILTER_DEFAULTS);
      setFilterString((prev: string[]) => [...prev.filter((f: string) => !isQuickFilterString(f)), ...quickParts]);
    } else {
      // Leaving map view: strip quick filter strings so table view is unaffected.
      // Keep mapQuickFilters state as-is (don't reset to defaults) so the save effect
      // doesn't overwrite the user's saved quick filter preferences.
      setFilterString((prev: string[]) => prev.filter((f: string) => !isQuickFilterString(f)));
    }
  };

  const handleSortingChange = getOnSortingChangeWithToast(sortingModel as any, setSortingModel as any);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Generic function to render highlighted cells (exactly like ProjectsListOld)
  const renderHighlightedCell = (fieldName: string, params: any) => {
    const highlights: string | undefined = params.row['@search.highlights']?.[fieldName]?.join(' '); // Get the highlight fragment
    const fullValue: string = (params.value as string) || ''; // Get the full value, fallback to empty string

    // If there are highlights, merge them into the full value
    let displayValue = fullValue;
    if (highlights) {
      const highlightedText = highlights.replace(
        /<em>(.*?)<\/em>/g,
        (_, match) => `<em style="background-color: yellow; font-style: normal; padding: 0 2px;">${match}</em>`,
      );

      // Extract plain text version of the highlight (removes HTML tags)
      const plainHighlight = highlights.replace(/<[^>]+>/g, '');

      // Replace the plain highlight in the full value with the styled highlight
      displayValue = fullValue.replace(plainHighlight, highlightedText);
    }

    // Tooltip should always show the plain text version of the full value
    const plainText = fullValue.replace(/<[^>]+>/g, '');

    return (
      <div
        className='MuiDataGrid-cellContent'
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={plainText} // Tooltip with the full value
        dangerouslySetInnerHTML={{
          __html: displayValue,
        }}
      />
    );
  };

  // Function to auto-show columns that contain search results (same logic as ProjectsListOld.tsx)
  const autoShowSearchResultColumns = React.useCallback(
    (searchResults: any[]) => {
      if (!searchResults || searchResults.length === 0) return;

      const columnsToShow = new Set<string>();

      searchResults.forEach(result => {
        if (result['@search.highlights']) {
          // Add columns that have highlights (search results)
          Object.keys(result['@search.highlights']).forEach(field => {
            columnsToShow.add(field);
          });
        }
      });

      // Update visible columns to include search result columns
      setVisibleColumns(prev => {
        const newVisibleColumns = new Set([...prev, ...Array.from(columnsToShow)]);
        return Array.from(newVisibleColumns);
      });

      // Also update the columnVisibility state to make these columns visible in MRT
      setColumnVisibility(prev => {
        const newColumnVisibility = { ...prev };
        Array.from(columnsToShow).forEach(column => {
          newColumnVisibility[column] = true;
        });
        return newColumnVisibility;
      });
    },
    [visibleColumns],
  );
  const clearSearchQuery = () => setSearchQuery('');

  const [isFilterOpen, setFilterOpen] = React.useState(false);

  const openFilter = () => setFilterOpen(true);
  const closeFilter = () => setFilterOpen(false);

  const [isWizardOpen, setIsWizardOpen] = React.useState(false);

  const handleOpenWizard = () => {
    setIsWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setIsWizardOpen(false);
  };

  // Updated function to handle sending a single project
  const handleSendSelectedProject = async (row: any) => {
    const projectId = row.Id;
    if (!projectId) {
      toast.error('Invalid project');
      return;
    }
    const reportName = `Cost Analysis Report: ${row.ProjectTitle} (${row.ProjectNumber})`;
    const reportGeneralInfo = {
      reportName: reportName,
    };
    handleGenerateReport(projectId, reportGeneralInfo, row.Id);
  };

  const handleOpenPayappUpload = useCallback(async (row: Project) => {
    const projectId = row.Id;
    if (!projectId) {
      toast.error('Invalid project');
      return;
    }
    setPayappUploadRowId(projectId);
    try {
      const response = await retrieveProjectDraws(projectId);
      const draws = (response?.draws || []) as DrawOption[];
      setPayappUploadProject({
        id: projectId,
        projectNumber: row.ProjectNumber || '',
        projectName: row.ProjectTitle || '',
      });
      setPayappUploadDraws(draws);
      setPayappUploadDialogOpen(true);
    } catch (error) {
      handleError(error);
    } finally {
      setPayappUploadRowId(null);
    }
  }, []);

  const handleClosePayappUploadDialog = useCallback(() => {
    setPayappUploadDialogOpen(false);
    setPayappUploadProject(null);
    setPayappUploadDraws([]);
  }, []);

  const handleOpenCreateMagicReport = useCallback(
    async (row: Project) => {
      const projectId = row.Id;
      if (!projectId) {
        toast.error('Invalid project');
        return;
      }
      setMagicReportRowId(projectId);
      try {
        await openDrawSelection(projectId);
      } finally {
        setMagicReportRowId(null);
      }
    },
    [openDrawSelection],
  );

  const handleMagicReportDrawConfirm = useCallback(
    async (drawIds: string[], hiddenCoLienDrawIds: string[]) => {
      await confirmDrawSelection(drawIds, hiddenCoLienDrawIds, {
        onConfirmReplace: () =>
          getConfirmation({
            heading: 'Change report draws',
            description:
              'Changing the selected draws will archive the current magic report and start a new one for the newly selected draws. Continue?',
            confirmButton: { label: 'Continue', color: 'error' },
            cancelButton: { label: 'Cancel', color: 'secondary' },
          }),
      });
    },
    [confirmDrawSelection, getConfirmation],
  );

  const handleGenerateReport = async (projectId: string, updatedGeneralInfo: ReportGeneralInfo, rowId: string) => {
    if (updatedGeneralInfo && !isValidGeneralInfo(updatedGeneralInfo)) {
      toast.error('Invalid report details. Please ensure all required fields are correctly filled.');
      return;
    }
    try {
      setLoadingRows(prev => new Set([...Array.from(prev), rowId]));
      setGenerateReportInProgress(true);
      const report = await generateReport(projectId, updatedGeneralInfo);
      navigate(`${ROUTES.CA_REPORTS}/${report.id}`, { state: { reportId: report.id } });
      toast.success('Report created successfully');
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingRows(prev => {
        const newLoading = new Set(prev);
        newLoading.delete(rowId);
        return newLoading;
      });
      setGenerateReportInProgress(false);
    }
  };

  const handleBulkDelete = async () => {
    const projectIds = selectedProjects;

    if (projectIds.length === 0) {
      toast.error('Please select at least one project');
      return;
    }

    setBulkDeleteInProgress(true);

    try {
      // First, check for projects with linked reports
      const linkedReportProjects = await FilterProjectsWithLinkedReports(projectIds);

      // Filter out ids of projects with linked reports
      const linkedReportProjectIds = linkedReportProjects.map((project: any) => project.projectId);

      // Filter out ids of project that can be deleted
      const deletableProjectIds = projectIds.filter(id => !linkedReportProjectIds.includes(id));

      let isConfirmed = false;

      if (linkedReportProjects.length > 0) {
        // If there are projects with linked reports
        isConfirmed = await getConfirmation({
          heading: 'Delete Projects',
          description: `
            The following projects have linked reports and cannot be deleted. Do you want to continue deleting the remaining projects?
            <ul>
              ${linkedReportProjects.map((project: any) => `<li>${project.projectNumber} - ${project.projectTitle}</li>`).join('')}
            </ul>
            `,
          confirmButton: {
            label: 'Continue',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
      } else {
        // If no projects have linked reports
        isConfirmed = await getConfirmation({
          heading: 'Delete Projects',
          description: 'Are you sure you want to delete these project(s)?',
          confirmButton: {
            label: 'Delete',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
      }

      if (isConfirmed && deletableProjectIds.length > 0) {
        await bulkDeleteProjects(deletableProjectIds);
        toast.success('Project(s) deleted successfully');
        setSelectedProjects([]);
      } else {
        toast.info('No projects were deleted.');
      }

      queryClient.invalidateQueries();
    } catch (error) {
      toast.error('Failed to delete projects');
    } finally {
      setBulkDeleteInProgress(false);
    }
  };

  const handleExportCsv = async () => {
    setCsvExportInProgress(true);
    try {
      // Only route through Azure Search when the user has an active search query or filters.
      // When nothing is active the backend exports directly from the DB (no Azure Search cap).
      const hasActiveSearch = debouncedSearchQuery.trim().length > 0 || filterString.length > 0;

      let searchOptionsString: string | undefined;
      if (hasActiveSearch) {
        const preProcessedSearchQuery = preProcessSearchText(debouncedSearchQuery);
        const searchRequestBody = buildSearchRequestBody(
          preProcessedSearchQuery,
          filterString,
          { page: 0, pageSize: 1000000 },
          sortingModel,
          true, // ignorePagination — backend will paginate Azure Search internally
          visibleColumns,
        );
        searchOptionsString = JSON.stringify(searchRequestBody);
      }

      const blob = await exportProjectsCsv(searchOptionsString);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `projects_${timestamp}.csv`;
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Projects exported successfully');
    } catch (error) {
      handleError(error);
    } finally {
      setCsvExportInProgress(false);
    }
  };

  const openDeleteConfirmation = (row: any) => {
    setProjectToDelete(row);
  };

  React.useEffect(() => {
    if (projectToDelete) {
      setShowDeleteDialog(true);
    } else {
      setShowDeleteDialog(false);
    }
  }, [projectToDelete]);

  const columns = React.useMemo<MRT_ColumnDef<Project>[]>(
    () => [
      ...(enableSingleSelection
        ? [
            {
              id: 'select',
              header: 'Select',
              size: 100,
              enableSorting: false,
              enableColumnFilter: false,
              Cell: ({ row }: { row: MRT_Row<Project> }) => (
                <Button
                  variant='outlined'
                  color='primary'
                  size='small'
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (onProjectsSelect) {
                      onProjectsSelect([row.original.Id]);
                    }
                  }}
                >
                  Select
                </Button>
              ),
            } as MRT_ColumnDef<Project>,
          ]
        : []),
      {
        accessorKey: 'ProjectNumber',
        header: 'Project Number',
        size: 130,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('ProjectNumber', { row: row.original, value: row.original.ProjectNumber });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'ProjectTitle',
        header: 'Project Title',
        size: 300,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          const highlight = row.original['@search.highlights']?.ProjectTitle?.[0] || row.original.ProjectTitle || '';
          const plainText = highlight.replace(/<[^>]+>/g, '');
          return (
            <Box p={1} width={'100%'} height={'100%'} display={'flex'} flexDirection={'column'} justifyContent={'space-evenly'}>
              <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-start'}>
                <CustomTooltip title={plainText}>
                  <Typography
                    variant='subtitle2'
                    fontWeight='bold'
                    maxWidth={row.original?.IsDraft ? '70%' : '100%'}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      '& em': {
                        backgroundColor: 'yellow',
                        fontStyle: 'normal',
                        padding: '0 2px',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: highlight }}
                  />
                </CustomTooltip>
                {row.original?.PreviewCoverImageBlobName && <PreviewPopoverIcon blobName={row.original?.PreviewCoverImageBlobName} />}
              </Stack>
              <Typography variant='caption'>
                {row.original.UpdatedOn
                  ? joinDateAndUser2(row.original.UpdatedOn, row.original.UpdatedBy, 'Updated On ')
                  : joinDateAndUser2(row.original.CreatedOn, row.original.CreatedBy, 'Uploaded On ')}
              </Typography>
            </Box>
          );
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'UnassignedBudget',
        header: 'Unallocated Budget',
        Cell: ({ row }: { row: MRT_Row<Project> }) => (
          <UnassignedBudgetCell totalBudget={row.original.Budget ?? 0} unassignedBudget={row.original.UnassignedBudget ?? 0} />
        ),
        size: 150,
        enableSorting: true,
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'ProjectTags',
        header: 'Tags',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          const tags = row.original.ProjectTags;
          if (!tags || tags.length === 0) return null;

          const tagDetails = row.original.ProjectTagDetails ?? [];
          const detailsMap: Record<string, string> = {};
          tags.forEach((name, i) => {
            if (tagDetails[i]) detailsMap[name] = tagDetails[i];
          });

          // Check if there are highlights for ProjectTags
          const highlights = row.original['@search.highlights']?.ProjectTags;

          const renderChip = (tag: string) => {
            const chip = (
              <Chip
                label={tag}
                size='small'
                variant='outlined'
                sx={{
                  color: TAG_CONFIG[tag]?.color || 'default',
                  backgroundColor: TAG_CONFIG[tag]?.backgroundColor || 'default',
                  borderColor: TAG_CONFIG[tag]?.backgroundColor || 'default',
                  fontWeight: 500,
                  margin: '0 !important',
                  ...(highlights?.some(h => h.toLowerCase().includes(tag.toLowerCase())) && {
                    backgroundColor: 'yellow',
                    color: 'black',
                    fontWeight: 'bold',
                  }),
                }}
              />
            );

            if ((tag === 'Bad Budget' || tag === 'Bad Project') && detailsMap[tag]) {
              return (
                <Tooltip key={tag} title={<span style={{ paddingInline: '0.5rem' }}>Reason: {detailsMap[tag]}</span>}>
                  {chip}
                </Tooltip>
              );
            }

            return <span key={tag}>{chip}</span>;
          };

          return (
            <Box height={'100%'} display={'flex'} alignItems={'center'}>
              <Stack direction={'row'} spacing={1} flexWrap={'wrap'} py={0.5} gap={0.5}>
                {tags.map(tag => tag && renderChip(tag))}
              </Stack>
            </Box>
          );
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'CommencementDate',
        header: 'Commencement Date',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          if (!row.original.CommencementDate) return '--';
          return formatISODate(row?.original?.CommencementDate);
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'RevisedCompletionDate',
        header: 'Completion Date',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          if (!row.original.RevisedCompletionDate) return '--';
          return formatISODate(row.original.RevisedCompletionDate);
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Status',
        header: 'Status',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Status', { row: row.original, value: row.original.Status });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'CreatedOn',
        header: 'Uploaded On',
        size: 150,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          if (!row.original.CreatedOn) return '--';
          return formatISODate(row.original.CreatedOn);
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'CreatedBy',
        header: 'Uploaded By',
        size: 150,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('CreatedBy', { row: row.original, value: row.original.CreatedBy || '--' });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'UpdatedOn',
        header: 'Last Updated',
        size: 150,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          const date = row.original.UpdatedOn || row.original.CreatedOn;
          if (!date) return '--';
          return formatISODate(date);
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'ProjectType',
        header: 'Project Type',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('ProjectType', { row: row.original, value: row.original.ProjectType });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Observer',
        header: 'Observer',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Observer', { row: row.original, value: row.original.Observer });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Budget',
        header: 'Total Budget',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => formatBudget(row.original.Budget?.toString(), true),
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Address',
        header: 'Address',
        size: 250,
        enableSorting: false,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          try {
            if (row.original.City == null) return null;
            const parts = row.original.City.split(',');
            const cityName = capitalize(parts[0]);
            const stateCode = parts[1].toUpperCase();
            const addressValue = [cityName, stateCode].filter(Boolean).join(', ');

            // Use the exact same logic as ProjectsListOld.tsx for Address column
            const highlight = row.original['@search.highlights']?.City?.[0] || addressValue || '';

            // Remove HTML tags for the tooltip
            const plainText = highlight.replace(/<[^>]+>/g, '');

            // Ensure `highlight` is a string before applying custom styles
            const highlightedContent =
              typeof highlight === 'string'
                ? highlight.replaceAll(/<em>/g, '<em style="background-color: yellow; font-style: normal; padding: 0 2px;">')
                : highlight;

            return (
              <div
                className='MuiDataGrid-cellContent'
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={plainText} // Use plain text for the tooltip
                dangerouslySetInnerHTML={{
                  __html: highlightedContent, // Render highlighted content with styled <em>
                }}
              />
            );
          } catch (error) {
            console.log(error);
            return row.original.City;
          }
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Contractor',
        header: 'Contractor',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Contractor', { row: row.original, value: row.original.Contractor });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'County',
        header: 'County',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('County', { row: row.original, value: row.original.County });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'CustomArea',
        header: 'Area',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('CustomArea', { row: row.original, value: row.original.CustomArea });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'PostalCode',
        header: 'Postal Code',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('PostalCode', { row: row.original, value: row.original.PostalCode });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'ConstructionType',
        header: 'Construction Type',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('ConstructionType', { row: row.original, value: row.original.ConstructionType });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Electrical',
        header: 'Electrical',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Electrical', { row: row.original, value: row.original.Electrical });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'ExteriorWalls',
        header: 'Exterior Walls',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('ExteriorWalls', { row: row.original, value: row.original.ExteriorWalls });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Foundation',
        header: 'Foundation',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Foundation', { row: row.original, value: row.original.Foundation });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'HVACScope',
        header: 'HVAC Scope',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('HVACScope', { row: row.original, value: row.original.HVACScope });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'HVACType',
        header: 'HVAC Type',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('HVACType', { row: row.original, value: row.original.HVACType });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Roof',
        header: 'Roof',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Roof', { row: row.original, value: row.original.Roof });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Superstructure',
        header: 'Super Structure',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Superstructure', { row: row.original, value: row.original.Superstructure });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'UniqueCostFactors1',
        header: 'Unique Cost Factors 1',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('UniqueCostFactors1', { row: row.original, value: row.original.UniqueCostFactors1 });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'UniqueCostFactors2',
        header: 'Unique Cost Factors 2',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('UniqueCostFactors2', { row: row.original, value: row.original.UniqueCostFactors2 });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'UniqueCostFactors3',
        header: 'Unique Cost Factors 3',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('UniqueCostFactors3', { row: row.original, value: row.original.UniqueCostFactors3 });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'OtherCostFactor',
        header: 'Other Cost Factor',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('OtherCostFactor', { row: row.original, value: row.original.OtherCostFactor });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Windows',
        header: 'Windows',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Windows', { row: row.original, value: row.original.Windows });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Client',
        header: 'Client',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Client', { row: row.original, value: row.original.Client });
        },
      } as MRT_ColumnDef<Project>,
      {
        accessorKey: 'Borrower',
        header: 'Borrower',
        size: 200,
        enableSorting: true,
        Cell: ({ row }: { row: MRT_Row<Project> }) => {
          return renderHighlightedCell('Borrower', { row: row.original, value: row.original.Borrower });
        },
      } as MRT_ColumnDef<Project>,
      {
        id: 'actions',
        header: '',
        size: 195,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        enableHiding: false,
        ...getActionsColumnStyling(),
        Header: ({ table }) => (
          <Stack sx={{ width: '100%' }} direction='row' spacing={1} alignItems='flex-end' justifyContent='center'>
            {showMagicReport && (
              <WhiteFab
                size='small'
                icon={<Description fontSize='small' />}
                textColor='primary'
                disabled={true}
                sx={{
                  opacity: 0,
                  pointerEvents: 'none',
                  visibility: 'hidden',
                  '&:hover': { opacity: 0, visibility: 'hidden' },
                  '&:focus': { opacity: 0, visibility: 'hidden' },
                }}
              />
            )}
            {/* Dummy WhiteFab to align with Upload PayApp button in rows */}
            <WhiteFab
              size='small'
              icon={<CloudUpload fontSize='small' />}
              textColor='primary'
              disabled={true}
              sx={{
                opacity: 0,
                pointerEvents: 'none',
                visibility: 'hidden',
                '&:hover': { opacity: 0, visibility: 'hidden' },
                '&:focus': { opacity: 0, visibility: 'hidden' },
              }}
            />
            {/* Dummy WhiteFab to align with Generate Cost Analysis button in rows */}
            <WhiteFab
              size='small'
              icon={<Assessment fontSize='small' />}
              textColor='primary'
              disabled={true}
              sx={{
                opacity: 0,
                pointerEvents: 'none',
                visibility: 'hidden',
                '&:hover': { opacity: 0, visibility: 'hidden' },
                '&:focus': { opacity: 0, visibility: 'hidden' },
              }}
            />
            <PermissionGate actionRole='DELETE_PROJECT'>
              <Tooltip title='Delete Selected Projects' arrow={false}>
                <span>
                  <WhiteFab
                    size='small'
                    icon={<DeleteIcon fontSize='small' />}
                    textColor='error'
                    onClick={handleBulkDelete}
                    disabled={selectedProjects.length === 0}
                    loading={bulkDeleteInProgress}
                  />
                </span>
              </Tooltip>
            </PermissionGate>
          </Stack>
        ),
        Cell: ({ row }: { row: MRT_Row<Project> }) => (
          <Stack direction='row' spacing={1} alignItems='flex-end' justifyContent='center'>
            {showMagicReport &&
              (row.original.MonitoringType === 'Magic' ? (
                <Tooltip title='Open/Create Magic Report' arrow={false}>
                  <span>
                    <WhiteFab
                      size='small'
                      icon={<Description fontSize='small' />}
                      textColor='primary'
                      disabled={selectedProjects.length > 0}
                      loading={magicReportRowId === row.id}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleOpenCreateMagicReport(row.original);
                      }}
                    />
                  </span>
                </Tooltip>
              ) : (
                <WhiteFab
                  size='small'
                  icon={<Description fontSize='small' />}
                  textColor='primary'
                  disabled={true}
                  sx={{
                    opacity: 0,
                    pointerEvents: 'none',
                    visibility: 'hidden',
                    '&:hover': { opacity: 0, visibility: 'hidden' },
                    '&:focus': { opacity: 0, visibility: 'hidden' },
                  }}
                />
              ))}
            <Tooltip title='Upload PayApp' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  icon={<CloudUpload fontSize='small' />}
                  textColor='primary'
                  disabled={selectedProjects.length > 0}
                  loading={payappUploadRowId === row.id}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleOpenPayappUpload(row.original);
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip title='Generate Cost Analysis' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  icon={<Assessment fontSize='small' />}
                  textColor='primary'
                  disabled={selectedProjects.length > 0}
                  loading={loadingRows.has(row.id)}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleSendSelectedProject(row.original);
                  }}
                />
              </span>
            </Tooltip>
            <PermissionGate actionRole='DELETE_PROJECT'>
              <Tooltip title='Delete Project' arrow={false}>
                <span>
                  <WhiteFab
                    size='small'
                    icon={<DeleteIcon fontSize='small' />}
                    textColor='error'
                    disabled={selectedProjects.length > 0}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      openDeleteConfirmation(row.original);
                    }}
                  />
                </span>
              </Tooltip>
            </PermissionGate>
          </Stack>
        ),
      } as MRT_ColumnDef<Project>,
    ],
    [
      enableSingleSelection,
      onProjectsSelect,
      selectedProjects.length,
      bulkDeleteInProgress,
      loadingRows,
      payappUploadRowId,
      handleOpenPayappUpload,
      showMagicReport,
      magicReportRowId,
      handleOpenCreateMagicReport,
    ],
  );

  const skippedFields = ['CreatedById', 'ObserverId'];
  const reportSkippedFields = [
    'CommencementDate',
    'RevisedCompletionDate',
    'CreatedOn',
    'CreatedBy',
    'City',
    'State',
    'PostalCode',
    'CreatedById',
    'ObserverId',
    'actions', // Assuming you want to skip 'actions' as well
  ];

  if (isFromReport) {
    skippedFields.push(...reportSkippedFields);
  }
  const filteredColumns = columns.filter(column => {
    const accessorKey = column.accessorKey as string | undefined;
    return accessorKey && !skippedFields.includes(accessorKey);
  });

  const handleFilterChange = (newFilterArray: any[] | '', rawFilters: any) => {
    // Extract the 'ObserverId' filter and map quick filter strings to preserve them
    const myProjectsFilter = filterString.find((f: string | string[]) => f.includes('ObserverId'));
    const activeQuickFilters = filterString.filter((f: string) => isQuickFilterString(f));

    if (newFilterArray && newFilterArray.length > 0) {
      const currentFilters = [...newFilterArray];

      if (myProjectsFilter) {
        currentFilters.push(myProjectsFilter);
      }
      // Preserve map quick filter strings so panel changes don't clear them
      activeQuickFilters.forEach((qf: string) => {
        if (!currentFilters.includes(qf)) currentFilters.push(qf);
      });
      setFilterString(currentFilters);
      setRawFilters(rawFilters);

      // Extract filtered column fields
      const filteredFields = Object.keys(rawFilters);

      // Ensure these columns are visible
      setVisibleColumns(prev => {
        const updatedColumns = new Set([...prev, ...filteredFields]);
        return Array.from(updatedColumns);
      });
    } else {
      const createdByFilters = myProjectsFilter ? [myProjectsFilter] : [];
      setFilterString([...createdByFilters, ...activeQuickFilters]);
      // Treat as reset: clear raw filters only; the effect will decide whether to save based on diff
      const cleared: any = {};
      setRawFilters(cleared);
    }
  };

  // Save filter preferences when user applies filters (FilterPanel calls handleFilterChange on apply)
  React.useEffect(() => {
    if (isFromReport) return;
    // Persist only when rawFilters is explicitly set by apply
    if (rawFilters !== undefined) {
      (async () => {
        try {
          const cleaned = { ...cleanFiltersObject(rawFilters), __mapQuickFilters: mapQuickFilters };
          const norm = stableStringify(cleaned);
          if (savedFilterPrefsNorm === norm) return;
          await saveUserFilterPreferences(PAGE_KEY, cleaned);
          setSavedFilterPrefsNorm(norm);
        } catch (e) {
          console.error('Failed to save filter preferences', e);
        }
      })();
    }
  }, [rawFilters, isFromReport, savedFilterPrefsNorm]);

  // Save map quick filters whenever they change
  const mapQuickFiltersRef = React.useRef(mapQuickFilters);
  React.useEffect(() => {
    if (isFromReport) return;
    if (mapQuickFilters === mapQuickFiltersRef.current) return;
    mapQuickFiltersRef.current = mapQuickFilters;
    (async () => {
      try {
        const currentPanelFilters = rawFilters !== undefined ? cleanFiltersObject(rawFilters) : {};
        await saveUserFilterPreferences(PAGE_KEY, { ...currentPanelFilters, __mapQuickFilters: mapQuickFilters });
      } catch (e) {
        console.error('Failed to save map quick filter preferences', e);
      }
    })();
  }, [mapQuickFilters, isFromReport]);

  const fetchProjectsWrapper = useCallback(
    async (searchQuery: string, filterString: any, ignorePagination: boolean) => {
      const preProcessedSearchQuery = preProcessSearchText(searchQuery);
      try {
        const searchRequestBody = buildSearchRequestBody(
          preProcessedSearchQuery,
          filterString,
          { page: paginationModel.pageIndex, pageSize: paginationModel.pageSize },
          sortingModel,
          ignorePagination,
          visibleColumns,
        );
        const response = await fetchProjects(searchRequestBody);
        return response ?? [];
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    [paginationModel, sortingModel, visibleColumns],
  );

  const isMapsTab = tabIndex === 1;

  const { data, isLoading, isFetching, isError, error } = useQuery(
    [dataKeys.allProjects, debouncedSearchQuery, filterString, paginationModel, sortingModel, isMapsTab],
    () => fetchProjectsWrapper(debouncedSearchQuery, filterString, isMapsTab),
    {
      keepPreviousData: true,
    },
  );

  // Auto-show columns when search results contain highlights (same as ProjectsListOld.tsx)
  React.useEffect(() => {
    if (data && data.value && Array.isArray(data.value) && debouncedSearchQuery) {
      autoShowSearchResultColumns(data.value);
    }
  }, [data, debouncedSearchQuery, autoShowSearchResultColumns]);

  const rowCount = React.useMemo(() => {
    if (data && data['@odata.count'] !== undefined) {
      rowCountRef.current = data['@odata.count'];
    }
    return rowCountRef.current;
  }, [data?.['@odata.count']]);

  React.useEffect(() => {
    if (searchParams.get('showOnlyMyProjects') === 'true') {
      toggleMyProjectsFilter();
    }
  }, [searchParams]);

  if (isError) handleError(error);

  // Handle projects and total values
  let projects: any[] = [];
  if (data && data.error) {
    toast.error(data?.error?.message);
    projects = [];
  } else {
    // Use the existing logic for setting projects
    projects = isError ? [] : data && data.value && Array.isArray(data.value) ? data.value : [];
  }

  const onMapListSelectionChange = React.useCallback(
    (selectedProjectsIds: string[]) => {
      setSelectedProjects(selectedProjectsIds);
      if (isFromReport && isMapsTab && onProjectsSelect) {
        onProjectsSelect(selectedProjectsIds, JSON.stringify(rawFilters));
      }
    },
    [projects, isFromReport, onProjectsSelect, filterString, isMapsTab],
  );

  const toggleMyProjectsFilter = () => {
    const userId = user?.id ?? '';
    const myProjectsFilter = `ObserverId eq '${userId}'`;
    let newFilterArray = [...filterString];
    if (Array.isArray(filterString)) {
      if (filterString.some(filter => filter === myProjectsFilter)) {
        newFilterArray = filterString.filter(filter => filter !== myProjectsFilter);
      } else {
        newFilterArray.push(myProjectsFilter);
      }
    }
    setFilterString(newFilterArray);
  };

  const myProjectsFilterOn = React.useMemo(() => {
    const userId = user?.id ?? '';
    const myProjectsFilter = `ObserverId eq '${userId}'`;
    if (Array.isArray(filterString)) {
      return filterString.some(filter => filter === myProjectsFilter);
    }
    return false;
  }, [filterString, user?.id]);

  const handleMapQuickFilterChange = (newValues: MapQuickFilterValues) => {
    setMapQuickFilters(newValues);
    const quickParts = buildQuickFilterStrings(newValues);
    // Keep non-quick-filter strings (MyProjects, and any from FilterPanel)
    const panelFilters = filterString.filter((f: string) => !isQuickFilterString(f));
    setFilterString([...panelFilters, ...quickParts]);
  };

  const handleMapResetAll = () => {
    setMapQuickFilters(MAP_QUICK_FILTER_EMPTY);
    const myProjectsFilter = filterString.find((f: string) => f.includes('ObserverId'));
    setFilterString(myProjectsFilter ? [myProjectsFilter] : []);
    setRawFilters({});
  };

  const getFiltersConfig = (): FilterConfig[] => {
    let newConfig = deepCopy(projectFiltersConfig);
    // In map view, the quick filter chips own these fields — exclude from the modal to avoid duplicates
    if (isMapsTab) {
      newConfig = newConfig.filter((f: any) => !MAP_QUICK_FILTER_FIELDS.has(f.propertyName));
    }
    const visible = new Set<string>([...(defaultOnDisplayFilters ?? [])]);
    // If we have saved filter prefs, also mark those keys to be on display
    if (savedFilterPrefs) {
      try {
        const parsed = JSON.parse(savedFilterPrefs);
        const cleaned = cleanFiltersObject(parsed);
        Object.keys(cleaned).forEach(k => visible.add(k));
      } catch (err) {
        console.error('Failed to parse saved filter preferences', err);
      }
    }
    newConfig.forEach((filter: any) => {
      filter.onDisplay = Boolean(filter.onDisplay) || visible.has(filter.propertyName);
    });
    return newConfig;
  };

  const table = useMaterialReactTable<Project>({
    ...commonMRTStyling,
    columns,
    data: projects,
    enableMultiRowSelection: !enableSingleSelection,
    enableSorting: true,
    renderBottomToolbarCustomActions: () => (
      <Stack direction='row' spacing={2} alignItems='center'>
        {!isFromReport && <Button onClick={handleOpenWizard}>Create New Project</Button>}
      </Stack>
    ),
    getRowId: (row: Project) => row.Id,
    onPaginationChange: setPaginationModel,
    onSortingChange: handleSortingChange,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: (updaterOrValue: any) => {
      const newColumnVisibility = typeof updaterOrValue === 'function' ? updaterOrValue(columnVisibility) : updaterOrValue;
      setColumnVisibility(newColumnVisibility);

      // Also update visibleColumns to keep them in sync
      const updatedVisibleColumns = Object.keys(newColumnVisibility).filter(field => newColumnVisibility[field] === true);
      setVisibleColumns(updatedVisibleColumns);
      if (!isFromReport) queuePersistPreferences(newColumnVisibility, columnPinning);
    },
    onColumnPinningChange: (updaterOrValue: any) => {
      const newPinning = typeof updaterOrValue === 'function' ? updaterOrValue(columnPinning) : updaterOrValue;
      setColumnPinning(newPinning);
      if (!isFromReport) queuePersistPreferences(columnVisibility, newPinning);
    },
    state: {
      pagination: paginationModel,
      sorting: sortingModel,
      columnVisibility,
      columnPinning,
      density: 'compact',
      isLoading: isLoading || isFetching,
      rowSelection,
    },
    manualSorting: true,
    rowCount: rowCount,
    pageCount: Math.ceil(rowCount / paginationModel.pageSize),
    muiTableBodyRowProps: ({ row }: { row: MRT_Row<Project> }) => {
      // Determine row background color
      let backgroundColor = '';

      if (isFromReport && disabledProjectsIds?.includes(row.original.Id)) {
        backgroundColor = '#f0f0f0';
      } else {
        // Use zebra striping for alternating rows
        backgroundColor = row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';
      }

      return {
        onClick: () => {
          const projectId = row.original.Id;
          const url = `${ROUTES.PROJECTS}/${projectId}`;
          window.open(url, '_blank');
        },
        sx: {
          cursor: 'pointer',
          backgroundColor: backgroundColor,
          '&:hover': {
            backgroundColor: theme => theme.palette.action.hover,
          },
          // Style pinned columns to match row background
          '& td[data-pinned="true"]::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: backgroundColor,
            zIndex: -1,
          },
          '& td[data-pinned="true"]': {
            zIndex: 5,
          },
        },
      };
    },
    muiTableBodyCellProps: ({ row }: { row: MRT_Row<Project> }) => ({
      sx: {
        border: 'none',
        backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
      },
    }),
    enableRowSelection: (row: MRT_Row<Project>) => !enableSingleSelection && !disabledProjectsIds?.includes(row.original.Id),
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['mrt-row-select', 'ProjectNumber', 'ProjectTitle'],
        right: ['actions'],
      },
    },
  });

  // Load user column preferences on mount (unless isFromReport)
  React.useEffect(() => {
    if (isFromReport) return;
    (async () => {
      try {
        const res = await getUserColumnPreferences(PAGE_KEY);
        const columnsJsonStr = res?.columnsJson;
        const filtersJsonStr = res?.filtersJson;
        if (columnsJsonStr) {
          const parsed = JSON.parse(columnsJsonStr);
          if (parsed?.visibility && typeof parsed.visibility === 'object') {
            setColumnVisibility(prev => ({ ...prev, ...parsed.visibility }));
            setVisibleColumns(
              Object.keys({ ...getInitialState(false).columnVisibility, ...parsed.visibility }).filter(
                k => (({ ...getInitialState(false).columnVisibility, ...parsed.visibility }) as any)[k] === true,
              ),
            );
          }
          if (parsed?.pinning && (parsed.pinning.left || parsed.pinning.right)) {
            setColumnPinning({ left: parsed.pinning.left ?? defaultPinning.left, right: parsed.pinning.right ?? defaultPinning.right });
          }
        }
        if (filtersJsonStr) {
          setSavedFilterPrefs(filtersJsonStr);
          try {
            const parsed = JSON.parse(filtersJsonStr);
            const norm = stableStringify(cleanFiltersObject(parsed));
            setSavedFilterPrefsNorm(norm);
            if (parsed.__mapQuickFilters) {
              const restored = { ...MAP_QUICK_FILTER_DEFAULTS, ...parsed.__mapQuickFilters };
              setMapQuickFilters(restored);
              if (isMapsTab) {
                const quickParts = buildQuickFilterStrings(restored);
                setFilterString((prev: string[]) => [...prev.filter((f: string) => !isQuickFilterString(f)), ...quickParts]);
              }
            }
          } catch (err) {
            console.error('Failed to parse saved filter preferences', err);
          }
        }
      } catch (e) {
        console.error('Failed to load column preferences', e);
      }
    })();
  }, [isFromReport, defaultPinning]);

  // Debounced persist
  const queuePersistPreferences = (visibility: MRT_VisibilityState, pinning: { left?: string[]; right?: string[] }) => {
    if (savePrefTimeout.current) clearTimeout(savePrefTimeout.current);
    savePrefTimeout.current = setTimeout(async () => {
      try {
        const payload = { visibility, pinning };
        await saveUserColumnPreferences(PAGE_KEY, payload);
      } catch (e) {
        console.error('Failed to save column preferences', e);
      }
    }, DEBOUNCE_DELAY_MS);
  };

  // Clean filters: remove empty arrays/strings and empty date ranges
  const cleanFiltersObject = (filters: any): any => {
    if (!filters || typeof filters !== 'object') return {};
    const cleaned: any = {};
    Object.keys(filters).forEach(key => {
      const val = filters[key];
      if (Array.isArray(val)) {
        if (val.length > 0) cleaned[key] = val;
        return;
      }
      if (val === null || val === undefined) return;
      if (typeof val === 'string') {
        if (val.trim() !== '') cleaned[key] = val.trim();
        return;
      }
      if (typeof val === 'object') {
        // Handle date range objects { start, end }
        const hasStart = val.start != null && String(val.start).trim() !== '';
        const hasEnd = val.end != null && String(val.end).trim() !== '';
        if ('start' in val || 'end' in val) {
          if (hasStart || hasEnd) cleaned[key] = { start: hasStart ? val.start : null, end: hasEnd ? val.end : null };
          return;
        }
        // Generic nested object: keep if it has any keys after cleaning
        const nested = cleanFiltersObject(val);
        if (Object.keys(nested).length > 0) cleaned[key] = nested;
        return;
      }
      // booleans/numbers
      cleaned[key] = val;
    });
    return cleaned;
  };

  // Stable stringify to compare objects irrespective of key order
  const stableStringify = (obj: any): string => {
    const sortObj = (input: any): any => {
      if (Array.isArray(input)) return input.map(sortObj);
      if (input && typeof input === 'object') {
        return Object.keys(input)
          .sort()
          .reduce((acc: any, key: string) => {
            acc[key] = sortObj(input[key]);
            return acc;
          }, {} as any);
      }
      return input;
    };
    return JSON.stringify(sortObj(obj));
  };

  // Keep selectedProjects in sync with MRT rowSelection for the list view
  React.useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter((id: string) => Boolean((rowSelection as any)[id]));
    setSelectedProjects(selectedIds);
    if (isFromReport && !isMapsTab && onProjectsSelect) {
      onProjectsSelect(selectedIds, JSON.stringify(rawFilters));
    }
  }, [rowSelection, isFromReport, isMapsTab, onProjectsSelect, rawFilters]);

  const getPreferences = () => {
    return getFromLocalStorage(cacheKey);
  };

  const savePreferences = () => {
    const state = table.getState();
    storeInLocalStorage(cacheKey, state);
  };

  const showManageColumns = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setColumnsMenuAnchorEl(e.currentTarget);
  };

  const CustomFooter = () => (
    <Stack p={1} direction='row' alignItems='center' justifyContent='space-between'>
      <Stack direction='row' spacing={2} alignItems='center'>
        {!isFromReport && (
          <>
            <Button onClick={handleOpenWizard}>Create New Project</Button>
          </>
        )}
      </Stack>
      <Box>{/* MRT handles pagination automatically */}</Box>
    </Stack>
  );

  const tabs = [
    {
      id: 'list',
      name: 'Detailed View',
      icon: <ViewList fontSize='medium' />,
      component: (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme => theme.palette.background.paper,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <MaterialReactTable table={table} />
        </Box>
      ),
    },
    {
      id: 'maps',
      name: 'Maps',
      icon: <Place fontSize='medium' />,
      component: !mapsEnabled ? (
        <p style={{ marginLeft: '20px', color: '#555', fontSize: '16px', fontStyle: 'italic' }}>Maps are disabled.</p>
      ) : (
        <ProjectListMap
          projects={projects}
          loading={isLoading || isFetching}
          listOptions={{
            checkboxSelection: isFromReport && !disableChecboxSelectionOnMaps,
            onProjectsSelect: onMapListSelectionChange,
            disabledProjectsIds: disabledProjectsIds,
          }}
        />
      ),
    },
  ];

  React.useEffect(() => {
    if (resolvedProjectDetails && isFromReport) {
      setSelectedProjectDetails(resolvedProjectDetails);
    }
  }, [resolvedProjectDetails, isFromReport]);

  const getProjectChipLabel = (projectId: string | number) => {
    const idStr = String(projectId);
    const details = selectedProjectDetails[idStr];
    if (details) return `${details.number} - ${details.title}`;
    return '';
  };

  const removeSelectedProject = (projectId: string | number) => {
    const updated = selectedProjects.filter(id => id !== projectId);
    setSelectedProjects(updated);

    // Update rowSelection to match the removed project
    const updatedRowSelection = { ...rowSelection };
    delete updatedRowSelection[projectId];
    setRowSelection(updatedRowSelection);

    if (isFromReport && onProjectsSelect) {
      onProjectsSelect(updated, JSON.stringify(rawFilters));
    }
  };

  return (
    <Box
      sx={{
        height: isFromReport ? (customHeight ? `${customHeight}px` : '100%') : `calc(100vh - ${NAVBAR_HEIGHT} - 50px)`,
        marginTop: 1,
        marginBottom: 3,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => {
          setFilterAnchorEl(null);
        }}
      >
        {/* <FilterPanel columns={columns} filterModel={filterModel} setFilterModel={setFilterModel} skippedFields={skippedFields} /> */}
      </Menu>
      <Stack direction={'row'} alignItems='center' justifyContent={'space-between'} p={1} spacing={2}>
        {!searchHidden && (
          <Stack alignSelf={'flex-end'} sx={{ width: isMapsTab ? 220 : undefined, flexGrow: isMapsTab ? 0 : 1, flexShrink: 0 }}>
            <TextField
              label='Search Projects'
              defaultValue=''
              value={searchQuery}
              onChange={handleSearchInputChange}
              fullWidth
              variant='standard'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    {searchQuery ? (
                      <IconButton onClick={clearSearchQuery} size='small'>
                        <Clear />
                      </IconButton>
                    ) : (
                      <Search fontSize='small' />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{ alignSelf: 'flex-end !important', fontSize: 'large' }}
            />
          </Stack>
        )}
        {isMapsTab && (
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 0 }}>
            <MapQuickFilters
              values={mapQuickFilters}
              onChange={handleMapQuickFilterChange}
              observerOptions={users.map((u: any) => ({ value: `${u.firstName} ${u.lastName}`, label: `${u.firstName} ${u.lastName}` }))}
              customAreaOptions={customAreaOptions.map(mapCustomAreaLookup).map(o => ({ label: o.label, value: String(o.value) }))}
              hasAdvancedFilters={filterString.some((f: string) => !isQuickFilterString(f) && !f.includes('ObserverId'))}
              onResetAll={handleMapResetAll}
            />
          </Box>
        )}
        <Stack direction={'row'} alignItems={'center'} spacing={0} flexGrow={isMapsTab ? 0 : 1} justifyContent={'end'}>
          {user ? (
            <Stack direction={'row'} alignItems={'center'} spacing={0}>
              <IconOverTextButton
                icon={<Person />}
                text='My Projects'
                tooltip={
                  myProjectsFilterOn
                    ? 'Currently showing your projects only. Click to view all projects.'
                    : 'Click to view your projects only'
                }
                onClick={toggleMyProjectsFilter}
                sunken={myProjectsFilterOn}
                showHoverEffect={true}
              />
            </Stack>
          ) : null}
          <IconOverTextButton
            icon={isMapsTab ? <ViewList /> : <Place />}
            text={isMapsTab ? 'Tabular View' : 'Maps View'}
            tooltip={isMapsTab ? 'Click to see tabular view' : 'Click to see map view'}
            onClick={() => handleTabChange(isMapsTab ? 0 : 1)}
            showHoverEffect={true}
          />

          {/* Separator */}
          <ButtonDivider />

          {/* Manage Columns Button */}
          <IconOverTextButton
            icon={<ViewColumnIcon />}
            text='Columns'
            tooltip='Manage Columns'
            onClick={showManageColumns}
            disabled={isMapsTab}
            showHoverEffect={false}
            wrapInSpan={true}
          />

          {/* Filter Button with Badge */}
          <Badge badgeContent={getAppliedFiltersCount(filterString)} color='primary' overlap='circular'>
            <IconOverTextButton icon={<FilterListIcon />} text='Filter' tooltip='Filter' onClick={openFilter} showHoverEffect={false} />
          </Badge>
          {!isFromReport && (
            <PermissionGate actionRole='EXPORT_PROJECTS_CSV'>
              <IconOverTextButton
                icon={<FileDownloadIcon />}
                text='Export CSV'
                tooltip='Export all projects as CSV'
                onClick={handleExportCsv}
                showHoverEffect={false}
                wrapInSpan={true}
                loading={csvExportInProgress}
              />
            </PermissionGate>
          )}
          <FilterPanel
            open={isFilterOpen}
            onClose={closeFilter}
            filtersConfig={getFiltersConfig()}
            onFilterChange={handleFilterChange}
            prevAppliedFilters={savedFilterPrefs ?? prevAppliedFilters}
          />
        </Stack>
      </Stack>
      {isFromReport && (
        <Collapse in={selectedProjects.length > 0} timeout={300} easing='cubic-bezier(0.25, 0.46, 0.45, 0.94)'>
          <Box px={1} pt={1} pb={1}>
            <Stack direction='row' alignItems='flex-start' spacing={1}>
              <SelectedProjectChips
                selectedIds={selectedProjects}
                resolvedDetails={
                  resolvedProjectDetails ||
                  Object.fromEntries(
                    projects.map(p => [
                      String(p.Id),
                      { number: p.ProjectNumber || 'Unknown Number', title: p.ProjectTitle || 'Unknown Title' },
                    ]),
                  )
                }
                onRemove={removeSelectedProject}
                showArrows
                allowDelete
              />
            </Stack>
          </Box>
        </Collapse>
      )}
      {tabs.map((tab, index) => (
        <TabPanel
          key={tab.id}
          value={tabIndex}
          index={index}
          style={{
            height: isFromReport ? `calc(100% - ${CONTROLS_HEIGHT}px${isFromReport && ' - 22px'})` : `calc(100% - ${CONTROLS_HEIGHT}px)`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {tabIndex === index && <>{tab.component}</>}
        </TabPanel>
      ))}
      <ProjectWizard open={isWizardOpen} onClose={handleCloseWizard} />
      {/* DeleteConfirmationComponent */}
      {projectToDelete && (
        <DeletionHandler
          open={showDeleteDialog}
          item={projectToDelete}
          deleteFunction={deleteProject}
          onDeleteConfirm={(id, message) => {
            queryClient.invalidateQueries(dataKeys.allProjects);
            toast.success(message);
            setShowDeleteDialog(false);
            setProjectToDelete(null);
          }}
          onClose={() => {
            setShowDeleteDialog(false);
            setProjectToDelete(null);
          }}
        />
      )}
      {/* Custom Manage Columns Menu */}
      <CustomManageColumnsMenu anchorEl={columnsMenuAnchorEl} setAnchorEl={setColumnsMenuAnchorEl} table={table} title='Manage Columns' />
      {/* Upload PayApp dialog (PayApp tab only, draws pre-fetched for selected project) */}
      <UploadDialog
        open={payappUploadDialogOpen}
        onClose={handleClosePayappUploadDialog}
        filesToAccept={{ 'application/pdf': ['.pdf'] }}
        projectId={payappUploadProject?.id}
        projectNumber={payappUploadProject?.projectNumber}
        projectName={payappUploadProject?.projectName}
        initialDraws={payappUploadDraws}
        hideTabs={true}
        defaultTab={1}
      />
      {showMagicReport && (
        <MagicReportDrawSelectDialog
          open={magicReportDrawSelectOpen}
          onClose={handleCloseMagicReportDrawSelect}
          draws={magicReportDraws}
          projectName={magicReportProject?.projectName}
          onConfirm={handleMagicReportDrawConfirm}
          isLoading={magicReportOpening}
          initiallySelectedDrawIds={magicReportExistingDrawIds}
          initiallyHiddenCoLienDrawIds={magicReportExistingHiddenCoLienDrawIds}
          hasExistingReport={hasExistingMagicReport}
        />
      )}
      {showMagicReport && requiredFieldsGapProjectId && requiredFieldsGapProjectSummary && (
        <RequiredFieldsGateDialog
          open={requiredFieldsGapOpen}
          projectId={requiredFieldsGapProjectId}
          missingFields={requiredFieldsGap}
          projectSummary={requiredFieldsGapProjectSummary}
          onSaved={retryAfterFieldsFixed}
          onCancel={closeRequiredFieldsGap}
        />
      )}
    </Box>
  );
}

const getDataGridStyles = (isFromReport: boolean | undefined, customHeight?: number) => {
  return {
    ...(isFromReport ? { height: customHeight || 650 } : {}),
    padding: 1,
    backgroundColor: (theme: Theme) => theme.palette.background.paper,
    borderRadius: '8px',
    overflow: 'hidden',
    '& .highlightedRow': {
      backgroundColor: '#f0f0f0', // or any other color you prefer
      '&:hover': {
        backgroundColor: '#f0f0f0', // keep the same color as the non-hover state
      },
      '& .projectTableRow': {
        cursor: 'none',
      },
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: (theme: Theme) => theme.palette.primary.main,
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      whiteSpace: 'normal',
      lineHeight: 1.2,
      height: 'auto',
      textOverflow: 'clip',
      maxHeight: 'unset !important',
    },
    '& .MuiDataGrid-cellCheckbox': {
      paddingLeft: 0.8,
    },
    '& .MuiDataGrid-footerContainer p': {
      marginBottom: '0px',
    },
    '& .sticky': {
      position: 'sticky',
      left: 0,
      zIndex: 2,
      backgroundColor: (theme: Theme) => theme.palette.background.paper,
    },
    '& .projectTableRow': {
      cursor: 'pointer',
    },
    '& .MuiTablePagination-root p': {
      marginBottom: 'unset',
    },
    '& .MuiDataGrid-booleanCell': {
      margin: 'auto',
    },
  };
};

export default ProjectsList;
